import {useState} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";

import {API} from "api";
import {CHANNELS, isContentTypeSelectable} from "data/products";
import {useChannelSettings, getChannelSettingsQueryKey} from "hooks/useChannelSettings";
import {applyStageOrder} from "libs/stageOrder";

/**
 * Admin control for the channel-wide Stage option order (the releases
 * channel's EAP/Release/Beta/RC dropdown). Which stage leads is a seasonal
 * editorial choice, so it is data, not code: the order saves to
 * /api/v3/channel-settings and every product's dropdown sorts by it.
 *
 * The list shows the UNION of the channel's selectable types — not the
 * current product's subset — because the saved order is one list for every
 * product; each dropdown shows its own subset in this order.
 */

interface StageOption {
  label: string;
  value: string;
}

/** Every selectable type of the channel, across all products, declared order. */
export function channelStageOptions(channelValue: string): StageOption[] {
  const channelObject = (CHANNELS as Array<{value: string; types?: StageOption[]}>).find(
    (ch) => ch.value === channelValue
  );
  return (channelObject?.types ?? [])
    .filter((type) => isContentTypeSelectable(type.value))
    .map(({label, value}) => ({label, value}));
}

/** `values` with the item at `index` moved one step up or down. */
export function moveStage(values: string[], index: number, delta: -1 | 1): string[] {
  const target = index + delta;
  if (target < 0 || target >= values.length) return values;
  const next = [...values];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export default function StageOrderSection({channel}: {channel: string}) {
  const queryClient = useQueryClient();
  const {data: settings, isPending: isLoading} = useChannelSettings(channel);
  const options = channelStageOptions(channel);

  // null = follow the server order; an array = unsaved local edit.
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const [status, setStatus] = useState<"saved" | "failed" | null>(null);

  const serverOrdered = applyStageOrder(options, settings?.stageOrder ?? null).map(
    (option) => option.value
  );
  const displayedValues = draftOrder ?? serverOrdered;
  const byValue = new Map(options.map((option) => [option.value, option]));
  const isDirty = draftOrder !== null && draftOrder.join(" ") !== serverOrdered.join(" ");

  const {mutate: saveOrder, isPending: isSaving} = useMutation({
    mutationFn: () => API.putChannelSettings({channel, stageOrder: displayedValues}),
    onSuccess: () => {
      setDraftOrder(null);
      setStatus("saved");
      queryClient.invalidateQueries({queryKey: getChannelSettingsQueryKey(channel)});
    },
    onError: (err) => {
      console.error("[StageOrderSection] Save failed:", err);
      setStatus("failed");
    },
  });

  if (options.length < 2) return null;

  return (
    <div className="default-scene-control stage-order-section">
      <span
        className="default-scene-caption"
        title="Display order of the Stage dropdown, for every product of this channel. The first option leads the dropdown — put the current season's stage there."
      >
        Stage order
      </span>
      <ol className="stage-order-list">
        {displayedValues.map((value, index) => (
          <li key={value} className="stage-order-item">
            <span className="stage-order-label">{byValue.get(value)?.label ?? value}</span>
            <span className="stage-order-buttons">
              <button
                type="button"
                className="stage-order-btn"
                aria-label={`Move ${byValue.get(value)?.label ?? value} up`}
                disabled={index === 0 || isSaving || isLoading}
                onClick={() => {
                  setStatus(null);
                  setDraftOrder(moveStage(displayedValues, index, -1));
                }}
              >
                ↑
              </button>
              <button
                type="button"
                className="stage-order-btn"
                aria-label={`Move ${byValue.get(value)?.label ?? value} down`}
                disabled={index === displayedValues.length - 1 || isSaving || isLoading}
                onClick={() => {
                  setStatus(null);
                  setDraftOrder(moveStage(displayedValues, index, 1));
                }}
              >
                ↓
              </button>
            </span>
          </li>
        ))}
      </ol>
      <div className="default-scene-row">
        <button
          className="default-scene-btn"
          onClick={() => saveOrder()}
          disabled={!isDirty || isSaving}
          title="Save this order for every user of the channel"
        >
          {isSaving ? "..." : "Save stage order"}
        </button>
        {isDirty && (
          <button
            className="default-scene-btn default-scene-undo"
            onClick={() => {
              setStatus(null);
              setDraftOrder(null);
            }}
            disabled={isSaving}
            title="Discard the unsaved reordering"
          >
            Discard
          </button>
        )}
      </div>
      {status === "saved" && <span className="default-scene-status success">Stage order saved</span>}
      {status === "failed" && (
        <span className="default-scene-status warning">Couldn&apos;t save the stage order</span>
      )}
    </div>
  );
}
